import { useState, useCallback, useEffect, Suspense, useContext } from "react";
import { Navbar } from "./components/Navbar";
import {
	AuthContext,
	DependentContextProvider,
	EligibleDependentContextProvider,
	DependentsContext,
	EnrollmentContextProvider,
	GroupContext,
	GroupEnrollmentContextProvider,
	PaycheckContext,
	ThemeContext,
	ThemeContextProvider,
	GroupOwnerContext,
	GroupOwnerContextProvider
} from "./contexts";
import { BrowserRouter, Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
	AddContacts,
	ChangePassword,
	ClaimList,
	Dependents,
	AddDependent,
	EOB,
	Home,
	AddFamilyMemberForm,
	Benefits,
	PreviewHome,
	Profile,
	AllNotifications,
	Enrollment,
	KsopForm,
	VEBEnrollment,
	HealthCard,
	IntercityGLTDForm,
	IntercityPlanAcknowledgement,
	IntercityHealthPlanForm,
	Documents,
	BOPHealthCard,
	BenefitsV2
} from "./components/pages";
import { NavbarPreview } from "./components/navbarPreview";
import { api } from "./utils/api";

import "./App.css";
import "./globalStyles/theme.css";
import "./globalStyles/paperForm.css";
import "./globalStyles/notificationItem.css";
import "./globalStyles/VEBTheme.css";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import ClaimDetailsPdf from "./components/pages/claim/EOB/claimLines/ClaimDetailsPdf/ClaimDetailsPdf";
import Redirection from "./components/auth/redirection/Redirection";
import { LogoSettings } from "./types/logoSettings.types";
import Dependent from "./components/pages/home/dependents/dependent/Dependent";
import FAQs from "./components/pages/FAQs/FAQs";
import ContactUs from "./components/pages/contactUs/ContactUs";
import { trackPromise } from "react-promise-tracker";
import AuthenticatedRoute from "./components/shared/authenticatedRoute/AuthenticatedRoute";
import MasterEnrollments from "./components/pages/enrollment/MasterEnrollments";
import IntercityHealthCard from "./components/pages/healthCard/IntercityHealthCard/IntercityHealthCard";
import HealthCardAlert from "./components/shared/healthCardNotActive/healthCardAlert/HealthCardAlert";
import { AutoLogout, HealthCardNotActive, RedirectionHealthCard } from "./components/shared";
import PersonalHealthTest from "./components/pages/PersonalHealthTest/PersonalHealthTest";
import ExplanationOfBenefitsV3 from "./components/pages/claim/explanationOfBenefits/ExplanationOfBenefitsV3";
import { TermedEmployeeNavbar } from "./components/terminatedEmployeeNavbar";
import ProgressiveLabHealthCard from "./components/pages/healthCard/ProgressiveLabHealthCard/ProgressiveLabHealthCard";
import Claim_V2_DetailsGrid from "./components/pages/claim_v2/ClaimStatusDetailsGrid_v2";
import ExplanationOfBenefits from "./components/pages/claim_v2/explanationOfBenefits/ExplanationOfBenefits";
import ExplanationOfBenefitsDetails from "./components/pages/claim_v2/explanationOfBenefits/ExplanationOfBenefits";
import ClaimStatus_v2 from "./components/pages/claim_v2/ClaimStatus";

// Test Build

const App = (): JSX.Element => {
	const [url, setUrl] = useState(window.location.pathname);
	const { groupOwner, setGroupOwner } = useContext(GroupOwnerContext);
	const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem("@jwt_member"));
	const { setMember } = useContext(AuthContext);
	const location = useLocation();
	const history = useHistory();
	const { setDependents } = useContext(DependentsContext);
	const [logoSettings, setLogoSettings] = useState<LogoSettings | null>(null);
	const { setTheme, theme } = useContext(ThemeContext);
	const [isVerified, setIsVerified] = useState<boolean>(false);
	const { setPaycheck } = useContext(PaycheckContext);
	const [terminatedEmployee, setTerminatedEmployee] = useState<boolean>(false);

	const getMemberPortalInitData = useCallback(async (): Promise<void> => {
		if (localStorage.getItem("@jwt_member") && localStorage.getItem("@jwt_member") !== "null") {
			if (localStorage.getItem("@role") === "DEPENDENT") {
				const memberPortalInitData = await trackPromise(api.pages.getDependentPortalInitData());
				if (memberPortalInitData.member.employee_status == "RELIEVED") {
					setTerminatedEmployee(true);
				}
				setMember(Object.assign({}, memberPortalInitData.member));
				if (memberPortalInitData.theme) {
					setTheme(Object.assign({}, memberPortalInitData.theme));
				} else {
					setTheme(
						Object.assign({}, theme, {
							group_number: memberPortalInitData.member.group_number
						})
					);
				}
				setLogoSettings(Object.assign({}, memberPortalInitData.logoSettings));
			} else {
				const groupOwnerToken = localStorage.getItem("@jwt_group_owner");
				//alert(groupOwnerToken);
				const memberPortalInitData = await trackPromise(api.pages.getMemberPortalInitData(groupOwnerToken));

				if (memberPortalInitData.member.employee_status == "RELIEVED") {
					setTerminatedEmployee(true);
				}
				setMember(Object.assign({}, memberPortalInitData.member));
				setPaycheck(Object.assign({}, memberPortalInitData.paycheck));
				if (memberPortalInitData.groupOwner) {
					setGroupOwner(Object.assign({}, memberPortalInitData.groupOwner));
				}
				if (memberPortalInitData.theme) {
					setTheme(Object.assign({}, memberPortalInitData.theme));
				} else {
					setTheme(
						Object.assign({}, theme, {
							group_number: memberPortalInitData.member.group_number
						})
					);
				}
				setDependents(Object.assign([], memberPortalInitData.dependents));
				setLogoSettings(Object.assign({}, memberPortalInitData.logoSettings));
			}
		} else {
			setTimeout(() => {
				getMemberPortalInitData();
			}, 1000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setMember, setTheme]);

	useEffect((): any => {
		if (history.location.pathname.split("/").includes("verify") === false) {
			getMemberPortalInitData();
		}

		return getMemberPortalInitData();
	}, [getMemberPortalInitData, history]);

	return (
		<Suspense fallback={<div />}>
			<div className="App">
				<BrowserRouter>
					<Suspense fallback={<div />}>
						{!terminatedEmployee ? (
							url.includes("/preview") ? (
								<NavbarPreview />
							) : (
								<Navbar logoSettings={logoSettings} />
							)
						) : (
							<TermedEmployeeNavbar logoSettings={logoSettings} />
						)}
					</Suspense>
					{/* <AutoLogout /> */}
					<Switch>
						<Route path="/verify" exact component={Redirection} />
						<Suspense fallback={<div />}>
							<AuthenticatedRoute
								path="/"
								exact
								Component={Home}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/home/add-family-member"
								exact
								Component={AddFamilyMemberForm}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/test-claim-grid"
								exact
								Component={ClaimDetailsPdf}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/change-password"
								exact
								Component={ChangePassword}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/preview"
								exact
								Component={PreviewHome}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/preview"
								exact
								Component={PreviewHome}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/benefits"
								exact
								Component={BenefitsV2}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/documents"
								exact
								Component={Documents}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/personal-health-test"
								exact
								Component={PersonalHealthTest}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<DependentContextProvider>
								<AuthenticatedRoute
									path="/dependent"
									exact
									Component={Dependent}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
								<AuthenticatedRoute
									path="/claims"
									exact
									Component={ClaimList}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
								<AuthenticatedRoute
									path="/claims_v2"
									exact
									Component={ClaimStatus_v2}
									isAuth={localStorage.getItem("@jwt_member")}
								/>

								<AuthenticatedRoute
									path="/claim_v2-status/EOB"
									exact
									Component={ExplanationOfBenefits}
									isAuth={localStorage.getItem("@jwt_member")}
								/>

								{groupOwner?.role === "EMPLOYER" &&
								(groupOwner.type === "ENROLLER ADMIN" || groupOwner.type === "AGENT") ? null : (
									<AuthenticatedRoute
										path="/claim/EOB"
										exact
										Component={ExplanationOfBenefitsV3}
										isAuth={localStorage.getItem("@jwt_member")}
									/>
								)}
								<AuthenticatedRoute
									path="/dependent/claim/EOB"
									exact
									Component={EOB}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
							</DependentContextProvider>
							<AuthenticatedRoute
								path="/profile"
								exact
								Component={Profile}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/preview"
								exact
								Component={PreviewHome}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/profile/add-contact"
								exact
								Component={AddContacts}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/dependents"
								exact
								Component={Dependents}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/dependents/add-dependent"
								exact
								Component={AddDependent}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/dependents/view-dependent"
								exact
								Component={AddDependent}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/health-card"
								exact
								Component={RedirectionHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/clinic-card"
								exact
								Component={RedirectionHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/ici-health-card"
								exact
								Component={IntercityHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/bop-health-card"
								exact
								Component={RedirectionHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/progressive-labs-health-card"
								exact
								Component={RedirectionHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/test-group-health-card"
								exact
								Component={RedirectionHealthCard}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							{/* ------------------------------------- (WORK IN PROGRESS) ---------------------------------------- */}
							<AuthenticatedRoute
								path="/ici-health-form"
								exact
								Component={IntercityHealthPlanForm}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/health-card-not-active"
								exact
								Component={HealthCardNotActive}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/GLTD-ici-form"
								exact
								Component={IntercityGLTDForm}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/ici-acknowledge"
								exact
								Component={IntercityPlanAcknowledgement}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							{/* ------------------------------------- (WORK IN PROGRESS) ---------------------------------------- */}
							<GroupEnrollmentContextProvider>
								<AuthenticatedRoute
									path="/enrollment"
									exact
									Component={MasterEnrollments}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
								<AuthenticatedRoute
									path="/test-group-enrollment"
									exact
									Component={MasterEnrollments}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
								<AuthenticatedRoute
									path="/progressive-labs-enrollment"
									exact
									Component={MasterEnrollments}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
								<AuthenticatedRoute
									path="/bop-enrollment"
									exact
									Component={MasterEnrollments}
									isAuth={localStorage.getItem("@jwt_member")}
								/>
							</GroupEnrollmentContextProvider>
							<AuthenticatedRoute
								path="/FAQ"
								exact
								Component={FAQs}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/contact-us"
								exact
								Component={ContactUs}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<AuthenticatedRoute
								path="/all-notifications"
								exact
								Component={AllNotifications}
								isAuth={localStorage.getItem("@jwt_member")}
							/>
							<EnrollmentContextProvider>
								<EligibleDependentContextProvider>
									<AuthenticatedRoute
										path="/VEB/enrollment"
										exact
										Component={VEBEnrollment}
										isAuth={localStorage.getItem("@jwt_member")}
									/>
								</EligibleDependentContextProvider>
							</EnrollmentContextProvider>
						</Suspense>
					</Switch>
				</BrowserRouter>
			</div>
		</Suspense>
	);
};

export default App;
